<template>
  <a-modal
    v-model="showModal"
    :title="isOnlyView ? 'View condition' : 'Edit condition'"
    :footer="null"
    width="600px"
    :destroyOnClose="true"
    class="condition-modal"
  >
    <a-form-model ref="conditionForm" :model="form">
      <Readonly
        :show="true"
        title="Must do"
        :value="targetConditionValue ? `${selectedConditionName} ${targetConditionValue}` :`${selectedConditionName}`"
        class="mb-4"
      />
      <a-tabs type="card" class="mb-3" @change="changeTranslate">
        <a-tab-pane
          v-for="(translation, translationIndex) in form.translations"
          :key="translationIndex"
          force-render
        >
          <template #tab>
            <DefaultLabelTab
              :name="translation.tab_name"
              :error_validate="form.translations[translationIndex].error_validate"
              :is_default="translation.is_default"
            />
          </template>
          <Readonly
            :show="isOnlyView"
            title="Name"
            :value="form.translations[translationIndex].name"
            :required="true"
            class="mb-4"
          >
            <a-form-model-item
              label="Name"
              :key="`name${translationIndex}`"
              :prop="`translations.${translationIndex}.name`"
              :colon="false"
              :rules="[
                { required: true, message: 'Name is required', trigger: ['change'] },
              ]"
            >
              <a-input
                v-model="form.translations[translationIndex].name"
                :ref="`input-${translationIndex}-name`"
                placeholder="Name"
                size="large"
                :disabled="loading"
                @focus="clearError(translationIndex)"
              />
            </a-form-model-item>
          </Readonly>
        </a-tab-pane>
      </a-tabs>
      <a-row class="footer-viewscreen footer d-flex justify-content-end">
        <a-button
          :loading="loading"
          @click="closeModal"
        >
          {{ isOnlyView ? 'OK' : 'Cancel' }}
        </a-button>
        <a-button
          v-if="!isOnlyView"
          type="primary"
          class="ml-2"
          :loading="loading"
          @click="onSubmit"
        >
          Save
        </a-button>
      </a-row>
    </a-form-model>
  </a-modal>
</template>

<script>
import Readonly from '@/components/custom/readonlyMode/readonly.vue'
import DefaultLabelTab from '@/views/apps/referral-program/components/DefaultLabelTab.vue'
import { getConditionName } from '@/views/apps/referral-program/referral-programs/composables/useGetConditionName.js'
import { waitForRef } from '@/composables/useWaitForRef.js'
import { subscribe, unsubscribe } from '@/composables/keydownEvent.js'

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    languagesUsed: {
      type: Array,
      default: () => [],
    },
    selectedCondition: {
      type: Object,
      default: null,
    },
    targetConditionValue: {
      type: [String, Number],
      default: '',
    },
    isOnlyView: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    DefaultLabelTab,
    Readonly,
  },
  data: () => ({
    showModal: false,
    form: {
      translations: [],
    },
    loading: false,
  }),
  watch: {
    show: {
      handler(val) {
        if (val) {
          this.showModal = true
        } else {
          this.showModal = false
        }
      },
      deep: true,
      immediate: true,
    },
    showModal: {
      handler(val) {
        if (!val) {
          this.closeModal()
        }
      },
      deep: true,
    },
  },
  computed: {
    selectedConditionName() {
      return getConditionName(this.selectedCondition?.type)
    },
  },
  methods: {
    changeTranslate(index) {
      this.focusInput(index)
    },
    async focusInput(index) {
      if (!this.isOnlyView) {
        await waitForRef(`input-${index}-name`, this)
          .then((inputRef) => {
            setTimeout(() => {
              inputRef[0].focus()
            }, 300)
          })
          .catch((err) => {
            console.warn('Не удалось получить $refs.input:', err)
          })
      }
    },
    templateDataFilling() {
      const translationsMap = new Map()

      // Предзаполняем карту переводов по language_id, если есть selectedCondition
      if (this.selectedCondition?.translations?.length) {
        this.selectedCondition.translations.forEach(t => {
          translationsMap.set(t.language_id, t.name)
        })
      }

      // Сортируем языки так, чтобы дефолтный был первым
      const sortedLanguages = [...this.languagesUsed].sort((a, b) => b.is_default - a.is_default)

      // Формируем массив translations
      this.form.translations = sortedLanguages.map(lang => ({
        language_id: lang.value,
        tab_name: lang.name,
        name: translationsMap.get(lang.value) || '',
        is_default: lang.is_default,
        error_validate: false,
      }))
    },
    checkingForTabFilling() {
      let isValidTranslations = true

      this.form.translations.forEach((translation, index) => {
        if (translation?.name?.length) {
          this.form.translations[index].error_validate = false
        } else {
          this.form.translations[index].error_validate = true
          isValidTranslations = false
        }
      })

      return isValidTranslations
    },
    onSubmit() {
      const isValidTranslations = this.checkingForTabFilling()

      this.$refs.conditionForm.validate(async (valid) => {
        if (!valid || !isValidTranslations) {
          this.$notification.error({
            message: 'Please fill in all required fields',
          })
          return false
        }
        this.update()
      })
    },
    async update() {
      this.$emit('update', { ...this.form, system_name: this.selectedCondition.system_name, index: this.selectedCondition.index })
      this.closeModal()
    },
    clearError(translationIndex) {
      this.form.translations[translationIndex].error_validate = false
    },
    clearForm() {
      this.form = {
        translations: [],
      }
    },
    closeModal() {
      this.$emit('close')
      setTimeout(() => {
        this.clearForm()
      }, 500)
    },
  },
  async mounted() {
    if (!this.isOnlyView) {
      this.focusInput(0)
    }
    this.templateDataFilling()
    subscribe(this.onSubmit)
  },
  beforeDestroy() {
    unsubscribe()
  },
}
</script>

<style lang="scss">
.condition-modal {
  .ant-tabs-card > .ant-tabs-content {
    margin-top: -16px;
  }

  .ant-tabs-card > .ant-tabs-content > .ant-tabs-tabpane {
    padding: 16px 8px 8px;
    border-right: 1px solid #e4e9f0;
    border-bottom: 1px solid #e4e9f0;
    border-left: 1px solid #e4e9f0;
    border-radius: 0 0 4px 4px;
  }

  .ant-tabs-card > .ant-tabs-bar .ant-tabs-tab {
    border-color: transparent;
    background: transparent;
    margin-right: 0 !important;
  }

  .footer {
    display: flex;
    justify-content: space-between;
    &::before,
    &::after {
      content: none;
    }
  }
}
</style>
